import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';
import { ISbStoryData } from '@storyblok/js';
import { TogglerComponent } from '../../../shared-extra/components/toggler/toggler.component';
import { getHeadingAnchor } from '../../get-header-anchor';
import { BlockStyle, BlockWrapperComponent } from '../block-wrapper/block-wrapper.component';
import { TableOfContentsItemListComponent } from '../table-of-contents-item-list/table-of-contents-item-list.component';

export interface TableOfContentItem {
  text: string;
  anchor?: string;
  level: number;
  children: TableOfContentItem[];
}

@Component({
  selector: 'app-table-of-contents',
  templateUrl: './table-of-contents.component.html',
  styleUrls: ['./table-of-contents.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [BlockWrapperComponent, TableOfContentsItemListComponent, TogglerComponent],
})
export class TableOfContentsComponent implements OnChanges {
  @Input() _uid: string; // just here to prevent errors
  @Input() component: string; // just here to prevent errors
  @Input() _editable: string;
  @Input() style: BlockStyle[];
  @Input() story: ISbStoryData;
  @Input() maxLevel: number;

  tableOfContents: TableOfContentItem[];

  ngOnChanges(): void {
    this.tableOfContents = [];
    const body = this.story?.content.body;
    if (!body) return;
    if (!Array.isArray(body)) {
      // article
      this.processTextItem({ content: body });
    } else {
      body.forEach(item => {
        this.processItem(item);
      });
    }
  }

  private processItem(item: (typeof this.story.content)[number]) {
    if (item.component === 'Text') {
      return this.processTextItem(item);
    }
    if (item.component === 'Title') {
      return this.processTitleItem(item);
    }
    if (item.component === 'ContentGroup') {
      return this.processContentGroupItem(item);
    }
  }

  private processContentGroupItem(item: (typeof this.story.content)[number]) {
    item.blocks.forEach(block => {
      this.processItem(block);
    });
  }

  private processTextItem(item: (typeof this.story.content)[number]) {
    item.content.content.forEach(subItem => {
      if (subItem.type !== 'heading' || !subItem.content) return;
      this.addContentItem({
        text: subItem.content.map(item => item.text).join(''),
        level: subItem.attrs.level,
        children: [],
      });
    });
  }

  private processTitleItem(item: (typeof this.story.content)[number]) {
    this.addContentItem({
      text: item.title,
      level: parseInt(item.type.replace('h', '')),
      children: [],
    });
  }

  private addContentItem(item: TableOfContentItem) {
    if (!item) return;
    if (item.level === 1) return;
    if (item.level > this.maxLevel) return;
    item.anchor = this.calcAnchor(item);
    const parentItem = this.findItemParent(item);
    if (!parentItem) {
      this.tableOfContents.push(item);
    } else {
      parentItem.children.push(item);
    }
  }

  private findItemParent(item: TableOfContentItem, items = this.tableOfContents) {
    const lastItem = items[items.length - 1];
    if (!lastItem || item.level <= lastItem.level) return;
    return this.findItemParent(item, lastItem.children) ?? lastItem;
  }

  private calcAnchor(item: TableOfContentItem): string {
    return getHeadingAnchor(item.level, item.text);
  }
}
